// With parallel consults a specialist's question is rendered inside its own
// card, which can be collapsed or scrolled far out of view — the turn would
// then sit blocked with nothing on screen explaining why. This is the
// thread-level signal that one exists, and a way back to it. It derives its
// list from the cards themselves rather than the thread-global interrupt slot,
// which stays reserved for the orchestrator's own interrupts.

import { useAuiState } from '@assistant-ui/react';
import type { ThreadMessage } from '@assistant-ui/react';
import { specialistCardAnchorId } from '@/shared/chat-runtime/specialistCardAnchor';

type Pending = { eventId: string; toolCallId: string; agentName: string };

function pendingInMessage(message: ThreadMessage): Pending[] {
  if (message.role !== 'assistant') return [];
  return message.content.flatMap((part) => {
    if (part.type !== 'tool-call' || !part.toolName.startsWith('Agent:') || !part.messages) return [];
    const agentName = part.toolName.slice('Agent:'.length);
    return part.messages.flatMap((nested) => nested.role !== 'assistant' ? [] : nested.content.flatMap((item) => {
      if (item.type !== 'data' || item.name !== 'specialist_question') return [];
      const data = item.data as { eventId?: string; answered?: boolean };
      return data.answered || !data.eventId ? [] : [{ eventId: data.eventId, toolCallId: part.toolCallId, agentName }];
    }));
  });
}

export function SpecialistQuestionBanner() {
  const messages = useAuiState((state) => state.thread.messages);
  const pending = messages.flatMap(pendingInMessage);
  if (pending.length === 0) return null;
  const current = pending[0];
  return (
    <div className="mx-auto my-2 flex w-full max-w-[70rem] flex-col gap-2 rounded-lg border border-primary/40 bg-card p-3">
      <p className="text-xs font-semibold text-muted-foreground">
        Argus needs your input · 1 of {pending.length} known {pending.length === 1 ? 'question' : 'questions'}
      </p>
      <button
        type="button"
        className="w-fit rounded-md border border-border px-3 py-1 text-sm transition-colors hover:bg-accent"
        onClick={() => document.getElementById(specialistCardAnchorId(current.toolCallId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
      >
        Answer {current.agentName}
      </button>
      {pending.length > 1 ? <p className="text-xs text-muted-foreground">Remaining questions will follow in sequence.</p> : null}
    </div>
  );
}
